import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Send, MapPin, Phone, Mail, Clock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { SITE } from '@/lib/site';
import Reveal from '@/components/site/Reveal';
import SectionHeading from '@/components/site/SectionHeading';

const INITIAL = { name: '', company: '', phone: '', email: '', property_type: 'office', requirement: '', message: '' };

export default function ContactSection({ compact = false }) {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error('Please fill in your name, email, and phone.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/leads', { ...form, source_page: 'contact' });
      toast.success('Thanks! Our team will reach out within 1 business day.');
      setForm(INITIAL);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="home-contact" className={`bg-white ${compact ? 'py-16' : 'py-20 md:py-28'}`}>
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionHeading kicker="Contact" title="Let’s discuss your property." subtitle="Share a brief about your asset or requirement and our team will get back to you within one business day." />
              <div className="mt-8 space-y-5 text-sm text-slate-700">
                <div className="flex gap-3"><MapPin className="mt-0.5 h-5 w-5 text-[#C6A35B]" /><span>{SITE.address}</span></div>
                <div className="flex gap-3"><Phone className="mt-0.5 h-5 w-5 text-[#C6A35B]" /><a className="hover:text-[#144726]" href={`tel:${SITE.phoneTel}`}>{SITE.phone}</a></div>
                <div className="flex gap-3"><Mail className="mt-0.5 h-5 w-5 text-[#C6A35B]" /><a className="hover:text-[#144726]" href={`mailto:${SITE.email}`}>{SITE.email}</a></div>
                <div className="flex gap-3"><Clock className="mt-0.5 h-5 w-5 text-[#C6A35B]" /><span>{SITE.hours}</span></div>
              </div>
              <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50 p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C6A35B]">For occupiers</p>
                <p className="mt-2 text-sm text-slate-600">Looking for office, retail or coworking space across Hyderabad? Our tenant representation team can shortlist the right options.</p>
                <Link to="/properties" className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[#144726] hover:text-[#C6A35B] transition-colors link-gold">Browse properties <ArrowRight className="h-4 w-4" /></Link>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={150}>
              <form onSubmit={submit} data-testid="contact-form" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm">
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input id="name" data-testid="contact-form-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your full name" />
                  </div>
                  <div>
                    <Label htmlFor="company">Company</Label>
                    <Input id="company" data-testid="contact-form-company-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Organization" />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input id="phone" data-testid="contact-form-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 …" />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" data-testid="contact-form-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" />
                  </div>
                  <div>
                    <Label>Property Type</Label>
                    <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v })}>
                      <SelectTrigger data-testid="contact-form-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="office">Office</SelectItem>
                        <SelectItem value="retail">Retail</SelectItem>
                        <SelectItem value="coworking">Coworking</SelectItem>
                        <SelectItem value="mixed-use">Mixed-Use</SelectItem>
                        <SelectItem value="warehouse">Warehouse</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Requirement</Label>
                    <Select value={form.requirement} onValueChange={(v) => setForm({ ...form, requirement: v })}>
                      <SelectTrigger data-testid="contact-form-requirement-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="landlord_leasing">I am a landlord — leasing</SelectItem>
                        <SelectItem value="landlord_management">I am a landlord — management</SelectItem>
                        <SelectItem value="occupier_search">I am an occupier — looking for space</SelectItem>
                        <SelectItem value="investor_advisory">Investor / advisory</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-5">
                  <Label htmlFor="message">Message</Label>
                  <Textarea id="message" data-testid="contact-form-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="A short brief about your asset or requirement…" rows={5} />
                </div>
                <div className="mt-6 flex items-center justify-between">
                  <p className="text-xs text-slate-500">We respect your privacy. We will never share your details.</p>
                  <Button type="submit" disabled={submitting} className="h-11 rounded-lg bg-[#144726] px-6 text-white hover:bg-[#0E3520]" data-testid="contact-form-submit-button">
                    {submitting ? 'Sending…' : (<><Send className="mr-2 h-4 w-4" /> Request Consultation</>)}
                  </Button>
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
